import { useState, useEffect, useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import {
  Loader,
  Plus,
  Pencil,
  Trash2,
  Server,
  Globe,
  Users,
  Zap,
  ChevronDown,
  ChevronRight,
  AlertCircle,
} from 'lucide-react';
import type { MinecraftServerStats } from '../../types/statusMonitor';
import {
  BuildServerLink,
  CreateBuildServerLinkRequest,
  UpdateBuildServerLinkRequest,
} from '../../types/buildTeam';
import { buildTeamApiService } from '../../services/buildTeamApi';
import { useMinecraftServers, getServersByCluster } from '../../hooks/useMinecraftServers';
import { CreatorProfile } from '../CreatorProfile';
import { usernameCacheService } from '../../services/usernameCache';
import { ConfirmationModal } from '../../components/ConfirmationModal';
import { SectionHeader } from '../ui/SectionHeader';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';
import { cn } from '../../utils/cn';

export interface BuildServersSectionProps {
  targetId: string;
  targetType: 'project' | 'build';
  canEdit: boolean;
  canDelete: boolean;
  onUpdate: () => void;
}

const LABEL_CLASS = 'text-xs font-medium text-glass-400 mb-1.5 block';

function clusterKey(link: BuildServerLink): string {
  return link.cluster_id?.trim() || '__none__';
}

function isOnline(mc: MinecraftServerStats): boolean {
  return Math.round(Date.now() - mc.last_seen * 1000) <= 180000;
}

function totalOnline(instances: MinecraftServerStats[]): number {
  return instances.filter(isOnline).length;
}

function totalPlayers(instances: MinecraftServerStats[]): number {
  return instances.reduce((sum, i) => (isOnline(i) ? sum + i.playerCount : sum), 0);
}

export function BuildServersSection({
  targetId,
  targetType,
  canEdit,
  canDelete,
  onUpdate,
}: BuildServersSectionProps) {
  const minecraftServers = useMinecraftServers();
  const [servers, setServers] = useState<BuildServerLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ type: 'delete'; id: string } | null>(null);

  const loadServers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data =
        targetType === 'project'
          ? await buildTeamApiService.getProjectServers(targetId)
          : await buildTeamApiService.getBuildServers(targetId);
      setServers(data);
      const linkedByIds = data.map((s) => s.linked_by).filter(Boolean);
      usernameCacheService.prefetchUsernames(linkedByIds);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load servers');
    } finally {
      setLoading(false);
    }
  }, [targetId, targetType]);

  useEffect(() => {
    loadServers();
  }, [loadServers]);

  const handleCreate = async (e: React.FormEvent, formData: CreateBuildServerLinkRequest) => {
    e.preventDefault();
    if (!canEdit || submitting) return;
    setSubmitting(true);
    try {
      if (targetType === 'project') {
        await buildTeamApiService.linkServerToProject(targetId, formData);
      } else {
        await buildTeamApiService.linkServerToBuild(targetId, formData);
      }
      setShowCreate(false);
      await loadServers();
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create server link');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (linkId: string, data: UpdateBuildServerLinkRequest) => {
    if (!canEdit || submitting) return;
    setSubmitting(true);
    try {
      await buildTeamApiService.updateServerLink(linkId, data);
      setEditingId(null);
      await loadServers();
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update server link');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (linkId: string) => {
    if (!canDelete || deletingId) return;
    setConfirmAction({ type: 'delete', id: linkId });
  };

  const executeConfirmAction = async () => {
    if (!confirmAction) return;
    setDeletingId(confirmAction.id);
    try {
      await buildTeamApiService.deleteServerLink(confirmAction.id);
      await loadServers();
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete server link');
    } finally {
      setDeletingId(null);
      setConfirmAction(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <SkeletonLoader className="h-4 w-28" />
          <SkeletonLoader className="h-9 w-28" />
        </div>
        <div className="space-y-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="subcard overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5">
                <SkeletonLoader className="h-4 w-32" />
                <SkeletonLoader className="h-5 w-20 rounded-full" />
              </div>
              <div className="p-3 space-y-2">
                <SkeletonLoader className="h-3 w-48" />
                <SkeletonLoader className="h-3 w-36" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        bare
        compact
        tone="danger"
        icon={AlertCircle}
        title="Failed to load servers"
        description={error}
        action={
          <button type="button" onClick={loadServers} className="glass-button">
            Retry
          </button>
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Server Links"
        icon={Server}
        count={servers.length}
        className="mb-0"
        actions={
          canEdit ? (
            <button
              type="button"
              onClick={() => setShowCreate(!showCreate)}
              className="glass-button"
            >
              <Plus className="w-4 h-4" />
              Add Server
            </button>
          ) : undefined
        }
      />

      {showCreate && canEdit && (
        <BuildServerCreateForm
          onSubmit={handleCreate}
          onCancel={() => setShowCreate(false)}
          submitting={submitting}
          minecraftServers={minecraftServers}
        />
      )}

      <BuildClusterGroupedServerList
        servers={servers}
        minecraftServers={minecraftServers}
        editingId={editingId}
        deletingId={deletingId}
        submitting={submitting}
        canEdit={canEdit}
        canDelete={canDelete}
        onEdit={setEditingId}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
      />

      {confirmAction && (
        <ConfirmationModal
          isOpen
          onClose={() => setConfirmAction(null)}
          onConfirm={executeConfirmAction}
          title="Delete Server Link"
          message="Are you sure you want to delete this server link?"
          confirmText="Delete"
          isLoading={!!deletingId}
        />
      )}
    </div>
  );
}

function BuildClusterGroupedServerList({
  servers,
  minecraftServers,
  editingId,
  deletingId,
  submitting,
  canEdit,
  canDelete,
  onEdit,
  onUpdate,
  onDelete,
}: {
  servers: BuildServerLink[];
  minecraftServers: import('../../types/statusMonitor').MinecraftServersData;
  editingId: string | null;
  deletingId: string | null;
  submitting: boolean;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: (id: string | null) => void;
  onUpdate: (linkId: string, data: UpdateBuildServerLinkRequest) => void;
  onDelete: (linkId: string) => void;
}) {
  const [collapsedClusters, setCollapsedClusters] = useState<Set<string>>(new Set());

  const linkedClusters = useMemo(() => {
    const linkedMap = new Map<string, BuildServerLink[]>();
    for (const server of servers) {
      const cid = clusterKey(server);
      if (!linkedMap.has(cid)) linkedMap.set(cid, []);
      linkedMap.get(cid)!.push(server);
    }

    return Array.from(linkedMap.entries())
      .sort(([a], [b]) => {
        if (a === '__none__') return 1;
        if (b === '__none__') return -1;
        return a.localeCompare(b);
      })
      .map(([clusterId, links]) => {
        const resolvedCluster = clusterId === '__none__' ? '' : clusterId;
        const liveInstances = resolvedCluster
          ? getServersByCluster(minecraftServers, resolvedCluster)
          : [];
        return { clusterId, displayId: clusterId === '__none__' ? '(no cluster)' : clusterId, links, liveInstances };
      });
  }, [servers, minecraftServers]);

  const toggleCluster = (clusterId: string) => {
    setCollapsedClusters((prev) => {
      const next = new Set(prev);
      if (next.has(clusterId)) next.delete(clusterId);
      else next.add(clusterId);
      return next;
    });
  };

  if (servers.length === 0) {
    return (
      <EmptyState
        bare
        compact
        icon={Server}
        title="No server links yet"
        description={canEdit ? 'Link a cluster or server to this record.' : 'Linked servers will appear here.'}
      />
    );
  }

  return (
    <div className="space-y-3">
      {linkedClusters.map(({ clusterId, displayId, links, liveInstances }) => {
        const isCollapsed = collapsedClusters.has(clusterId);
        const hasLive = liveInstances.length > 0;
        const online = totalOnline(liveInstances);
        const players = totalPlayers(liveInstances);

        return (
          <div key={clusterId} className="subcard overflow-hidden">
            <button
              type="button"
              onClick={() => toggleCluster(clusterId)}
              className="w-full flex items-center justify-between gap-2 px-4 py-2.5 hover:bg-glass-200/5 transition-colors"
              aria-expanded={!isCollapsed}
            >
              <div className="flex items-center gap-2 min-w-0">
                {isCollapsed ? (
                  <ChevronRight className="w-4 h-4 text-glass-400 shrink-0" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-glass-400 shrink-0" />
                )}
                <Server className="w-4 h-4 text-accent-300 shrink-0" />
                <span className="font-mono font-medium text-white text-sm truncate">{displayId}</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end shrink-0">
                <span
                  className={cn(
                    'badge badge-dot',
                    hasLive
                      ? online === liveInstances.length
                        ? 'badge-success'
                        : online > 0
                          ? 'badge-amber'
                          : 'badge-danger'
                      : 'badge-neutral'
                  )}
                >
                  {hasLive ? `${online}/${liveInstances.length} online` : 'no live match'}
                </span>
                {hasLive && players > 0 && (
                  <span className="text-xs text-glass-400 flex items-center gap-1 tabular-nums">
                    <Users className="w-3 h-3" /> {players}
                  </span>
                )}
              </div>
            </button>

            {!isCollapsed && (
              <div className="px-3 pb-3 pt-1 border-t border-glass-200/10 space-y-2">
                {links.map((server) => {
                  if (editingId === server.id && canEdit) {
                    return (
                      <div
                        key={server.id}
                        className="p-3 rounded-lg bg-glass-100/5 border border-glass-200/10"
                      >
                        <BuildServerEditForm
                          server={server}
                          onSave={(data) => onUpdate(server.id, data)}
                          onCancel={() => onEdit(null)}
                          submitting={submitting}
                          minecraftServers={minecraftServers}
                        />
                      </div>
                    );
                  }
                  return (
                    <div
                      key={server.id}
                      className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between px-1 py-2 border-b border-glass-200/10 last:border-0"
                    >
                      <div className="space-y-1 text-xs min-w-0">
                        {server.fqdn && (
                          <span className="inline-flex items-center gap-1 font-mono text-glass-300">
                            <Globe className="w-3 h-3 text-glass-500 shrink-0" />
                            {server.fqdn}
                          </span>
                        )}
                        {server.server_name && (
                          <div className="text-sm text-white font-medium">{server.server_name}</div>
                        )}
                        {server.notes && (
                          <p className="text-glass-400 italic">{server.notes}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-glass-500">
                          <span className="tabular-nums">
                            Linked {format(new Date(server.linked_at), 'PP')}
                          </span>
                          <span className="flex items-center gap-1">
                            by <CreatorProfile creatorId={server.linked_by} inline />
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => onEdit(server.id)}
                            className="btn-icon btn-icon-sm touch-manipulation"
                            title="Edit"
                            aria-label="Edit server link"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            type="button"
                            onClick={() => onDelete(server.id)}
                            disabled={deletingId === server.id}
                            className="btn-icon btn-icon-sm text-red-400 hover:text-red-300 touch-manipulation"
                            title="Delete"
                            aria-label="Delete server link"
                          >
                            {deletingId === server.id ? (
                              <Loader className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="w-3.5 h-3.5" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}

                {hasLive ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                    {liveInstances.map((mc) => (
                      <BuildLiveInstanceChip key={`${mc.clusterId}-${mc.serverId}`} stats={mc} />
                    ))}
                  </div>
                ) : (
                  clusterId !== '__none__' && (
                    <div className="flex items-center gap-2 py-2 px-1">
                      <div className="w-2 h-2 rounded-full bg-glass-400 flex-shrink-0" />
                      <span className="text-xs text-glass-500">
                        No live instances for this cluster in status monitor
                      </span>
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function BuildLiveInstanceChip({ stats }: { stats: MinecraftServerStats }) {
  const timeSinceLastSeen = Math.round(Date.now() - stats.last_seen * 1000);
  const offline = timeSinceLastSeen > 180000;

  return (
    <div
      className={cn(
        'rounded-md border p-2 text-[11px]',
        offline ? 'border-red-500/30 bg-red-500/5' : 'border-glass-200/10 bg-glass-100/5'
      )}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <div className={cn('w-1.5 h-1.5 rounded-full shrink-0', offline ? 'bg-red-500' : 'bg-green-500')} />
          <span className="font-mono text-glass-200 truncate">
            {stats.clusterId} — {stats.serverId}
          </span>
        </div>
        {!offline && (
          <span className="text-glass-400 shrink-0 flex items-center gap-0.5 tabular-nums">
            <Zap className="w-2.5 h-2.5" />
            {stats.tps.toFixed(0)} tps
          </span>
        )}
      </div>
      <div className="text-glass-500 tabular-nums">
        {offline ? 'Offline' : `${stats.playerCount}/${stats.maxPlayers} players · MSPT ${stats.mspt.toFixed(1)}ms`}
      </div>
    </div>
  );
}

function BuildServerCreateForm({
  onSubmit,
  onCancel,
  submitting,
  minecraftServers,
}: {
  onSubmit: (e: React.FormEvent, data: CreateBuildServerLinkRequest) => void;
  onCancel: () => void;
  submitting: boolean;
  minecraftServers: import('../../types/statusMonitor').MinecraftServersData;
}) {
  const [fqdn, setFqdn] = useState('');
  const [clusterId, setClusterId] = useState('');
  const [serverName, setServerName] = useState('');
  const [notes, setNotes] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const allClusterIds = useMemo(() => {
    const ids = new Set<string>();
    for (const srv of Object.values(minecraftServers)) {
      if (srv.clusterId) ids.add(srv.clusterId);
    }
    return Array.from(ids).sort((a, b) => a.localeCompare(b));
  }, [minecraftServers]);

  const filteredSuggestions = useMemo(() => {
    if (!clusterId.trim()) return allClusterIds;
    const q = clusterId.toLowerCase();
    return allClusterIds.filter((id) => id.toLowerCase().includes(q));
  }, [clusterId, allClusterIds]);

  const matchedInstances = useMemo(() => {
    if (!clusterId.trim()) return [];
    return getServersByCluster(minecraftServers, clusterId.trim());
  }, [clusterId, minecraftServers]);

  const handleSelectCluster = (id: string) => {
    setClusterId(id);
    setShowSuggestions(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!clusterId.trim()) return;
    onSubmit(e, {
      fqdn: fqdn.trim() || undefined,
      cluster_id: clusterId.trim(),
      server_name: serverName.trim() || undefined,
      notes: notes.trim() || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="subcard p-4 space-y-3">
      <div className="relative">
        <label className={LABEL_CLASS}>Cluster ID</label>
        <input
          type="text"
          value={clusterId}
          onChange={(e) => {
            setClusterId(e.target.value);
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          placeholder="Type to search live clusters…"
          className="glass-input w-full font-mono"
          autoComplete="off"
          required
        />
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 glass-card overflow-hidden z-10 max-h-48 overflow-y-auto divide-y divide-glass-200/10">
            {filteredSuggestions.map((id) => {
              const instances = getServersByCluster(minecraftServers, id);
              const onlineCount = instances.filter(
                (i) => Math.round(Date.now() - i.last_seen * 1000) <= 180000
              ).length;
              const playerCount = instances.reduce((sum, i) => {
                if (Math.round(Date.now() - i.last_seen * 1000) > 180000) return sum;
                return sum + i.playerCount;
              }, 0);
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => handleSelectCluster(id)}
                  className={cn(
                    'w-full text-left px-3 py-2 hover:bg-glass-200/5 transition-colors flex items-center justify-between gap-2',
                    id === clusterId.trim() && 'bg-accent-500/10'
                  )}
                >
                  <div className="flex items-center gap-2">
                    <Server className="w-3.5 h-3.5 text-glass-400" />
                    <span className="text-sm font-mono text-white">{id}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        'badge',
                        onlineCount === instances.length
                          ? 'badge-success'
                          : onlineCount > 0
                            ? 'badge-amber'
                            : 'badge-danger'
                      )}
                    >
                      {onlineCount}/{instances.length}
                    </span>
                    {playerCount > 0 && (
                      <span className="text-xs text-glass-400 tabular-nums">{playerCount}p</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {matchedInstances.length > 0 && (
        <div className="rounded-lg border border-accent-500/20 bg-accent-500/5 p-2.5">
          <p className="text-[11px] font-medium text-accent-300 mb-1.5 flex items-center gap-1">
            <Zap className="w-3 h-3" />
            Matched {matchedInstances.length} live instance{matchedInstances.length !== 1 ? 's' : ''}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {matchedInstances.map((mc) => {
              const offline = Math.round(Date.now() - mc.last_seen * 1000) > 180000;
              return (
                <span
                  key={`${mc.clusterId}-${mc.serverId}`}
                  className={cn(
                    'chip',
                    offline
                      ? 'border-red-500/30 bg-red-500/10 text-red-300'
                      : 'text-glass-200'
                  )}
                >
                  <div
                    className={cn(
                      'w-1.5 h-1.5 rounded-full flex-shrink-0',
                      offline ? 'bg-red-500' : 'bg-green-500'
                    )}
                  />
                  <span className="font-mono">{mc.serverId}</span>
                  {!offline && (
                    <span className="text-glass-500 tabular-nums">
                      {mc.playerCount}p · {mc.tps.toFixed(0)}tps
                    </span>
                  )}
                </span>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <label className={LABEL_CLASS}>FQDN</label>
        <input
          type="text"
          value={fqdn}
          onChange={(e) => setFqdn(e.target.value)}
          placeholder="server.example.com (optional)"
          className="glass-input w-full font-mono"
        />
      </div>
      <div>
        <label className={LABEL_CLASS}>Server Name</label>
        <input
          type="text"
          value={serverName}
          onChange={(e) => setServerName(e.target.value)}
          placeholder="Optional"
          className="glass-input w-full"
        />
      </div>
      <div>
        <label className={LABEL_CLASS}>Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional"
          rows={2}
          className="glass-input w-full resize-none"
        />
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="glass-button-secondary">
          Cancel
        </button>
        <button type="submit" disabled={submitting} className="btn-primary">
          {submitting ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Add link
        </button>
      </div>
    </form>
  );
}

function BuildServerEditForm({
  server,
  onSave,
  onCancel,
  submitting,
  minecraftServers,
}: {
  server: BuildServerLink;
  onSave: (data: UpdateBuildServerLinkRequest) => void;
  onCancel: () => void;
  submitting: boolean;
  minecraftServers: import('../../types/statusMonitor').MinecraftServersData;
}) {
  const [fqdn, setFqdn] = useState(server.fqdn || '');
  const [clusterId, setClusterId] = useState(server.cluster_id || '');
  const [serverName, setServerName] = useState(server.server_name || '');
  const [notes, setNotes] = useState(server.notes || '');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const allClusterIds = useMemo(() => {
    const ids = new Set<string>();
    for (const srv of Object.values(minecraftServers)) {
      if (srv.clusterId) ids.add(srv.clusterId);
    }
    return Array.from(ids).sort((a, b) => a.localeCompare(b));
  }, [minecraftServers]);

  const filteredSuggestions = useMemo(() => {
    if (!clusterId.trim()) return allClusterIds;
    const q = clusterId.toLowerCase();
    return allClusterIds.filter((id) => id.toLowerCase().includes(q));
  }, [clusterId, allClusterIds]);

  const matchedInstances = useMemo(() => {
    if (!clusterId.trim()) return [];
    return getServersByCluster(minecraftServers, clusterId.trim());
  }, [clusterId, minecraftServers]);

  const handleSelectCluster = (id: string) => {
    setClusterId(id);
    setShowSuggestions(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      fqdn: fqdn.trim() || null,
      cluster_id: clusterId.trim() || null,
      server_name: serverName.trim() || null,
      notes: notes.trim() || null,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="relative">
        <label className={LABEL_CLASS}>Cluster ID</label>
        <input
          type="text"
          value={clusterId}
          onChange={(e) => {
            setClusterId(e.target.value);
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          className="glass-input w-full font-mono"
          autoComplete="off"
        />
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 glass-card overflow-hidden z-10 max-h-48 overflow-y-auto divide-y divide-glass-200/10">
            {filteredSuggestions.map((id) => (
              <button
                key={id}
                type="button"
                onClick={() => handleSelectCluster(id)}
                className={cn(
                  'w-full text-left px-3 py-2 hover:bg-glass-200/5 flex items-center gap-2',
                  id === clusterId.trim() && 'bg-accent-500/10'
                )}
              >
                <Server className="w-3.5 h-3.5 text-glass-400" />
                <span className="text-sm font-mono text-white">{id}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {matchedInstances.length > 0 && (
        <div className="rounded-lg border border-accent-500/20 bg-accent-500/5 p-2.5">
          <p className="text-[11px] font-medium text-accent-300 mb-1.5">Live instances</p>
          <div className="flex flex-wrap gap-1.5">
            {matchedInstances.map((mc) => {
              const offline = Math.round(Date.now() - mc.last_seen * 1000) > 180000;
              return (
                <span
                  key={`${mc.clusterId}-${mc.serverId}`}
                  className={cn(
                    'chip font-mono',
                    offline ? 'border-red-500/30 text-red-300' : 'text-glass-200'
                  )}
                >
                  {mc.serverId}
                </span>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <label className={LABEL_CLASS}>FQDN</label>
        <input
          type="text"
          value={fqdn}
          onChange={(e) => setFqdn(e.target.value)}
          className="glass-input w-full font-mono"
        />
      </div>
      <div>
        <label className={LABEL_CLASS}>Server Name</label>
        <input
          type="text"
          value={serverName}
          onChange={(e) => setServerName(e.target.value)}
          className="glass-input w-full"
        />
      </div>
      <div>
        <label className={LABEL_CLASS}>Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="glass-input w-full resize-none"
        />
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="glass-button-secondary">
          Cancel
        </button>
        <button type="submit" disabled={submitting} className="btn-primary">
          {submitting ? <Loader className="w-4 h-4 animate-spin" /> : null}
          {submitting ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}
